<script>
  // Root component. Replaces SvelteKit's app shell + layout-cascade.
  //
  //   1. Initialises @auth0/auth0-spa-js on mount.
  //   2. Handles the Auth0 redirect callback (?code=...&state=...).
  //   3. Renders <Shell> (header + sidebar) around every route.
  //   4. Wraps protected routes in <AuthGuard>. Public routes (login, about,
  //      regulatory, unauthorized, embed/regulatory) skip the guard.
  //
  // The route table comes from src/routesManifest.json, generated by the
  // SvelteKit-to-SPA conversion.

  import { onMount } from 'svelte';
  import { Router, Route, navigate } from 'svelte-routing';

  import {
    handleRedirectCallback,
    refreshAuthState,
    isLoading
  } from './lib/auth.js';

  // Bridges the new SDK's auth state to the legacy stores/sessionStore so the
  // ~56 legacy pages reading sessionStore see the authenticated user. Without
  // this, Index.svelte (and similar) see isAuthenticated=false from the
  // legacy store and immediately redirect to /login → "stuck on sign-in"
  // loop after successful Auth0 login. See lib/sessionBridge.js.
  import { syncSessionFromAuth0 } from './lib/sessionBridge.js';

  import AuthGuard from './lib/guards/AuthGuard.svelte';
  import Shell from './lib/Shell.svelte';

  // Vite glob import — pages are statically loaded so route lookup is sync.
  const pageModules = import.meta.glob('./pages/**/*.svelte', { eager: true });

  // Each entry: { "routeDir": "users", "component": "Users.svelte",
  //               "importPath": "./pages/Users.svelte" }
  import manifest from './routesManifest.json';

  // Routes that may be visited without a session.
  const PUBLIC_ROUTES = new Set([
    'login',
    'about',
    'regulatory',
    'unauthorized',
    'embed/regulatory'
  ]);

  function resolveComponent(importPath) {
    const mod = pageModules[importPath];
    return mod ? mod.default : null;
  }

  // Most-specific paths first so prefix overlaps don't shadow deeper routes
  // (e.g. embed/regulatory must beat /regulatory).
  const sortedRoutes = [...manifest].sort(
    (a, b) => b.routeDir.split('/').length - a.routeDir.split('/').length
  );

  let bootError = null;

  onMount(async () => {
    try {
      await handleRedirectCallback();
      await refreshAuthState();
      // Bridge the SDK state into the legacy session store BEFORE
      // releasing isLoading. If we let isLoading flip to false earlier
      // (e.g. inside refreshAuthState), Svelte's reactivity mounts the
      // Router → Index.svelte → which reads sessionStore.isAuthenticated
      // (still false) → redirects to /login. By gating isLoading on the
      // bridge completion, Index.svelte sees the populated store from the
      // first render.
      await syncSessionFromAuth0();
      isLoading.set(false);
    } catch (e) {
      // Most common cause: the /oauth/token code-exchange returned a
      // non-200 (e.g. invalid_grant, audience mismatch, or the code was
      // already consumed because Auth0 re-fired the redirect). The user
      // needs to be able to retry — clear the URL of any leftover
      // ?code=... params, drop the loading flag, and route to /login
      // with a visible error message.
      // eslint-disable-next-line no-console
      console.error('Auth bootstrap failed:', e);
      bootError = e?.message || String(e);
      isLoading.set(false);
      try {
        // Strip ?code= / ?state= / ?error= so a refresh doesn't replay
        // the broken callback.
        const cleanPath = window.location.pathname;
        window.history.replaceState({}, document.title, cleanPath);
      } catch {/* ignore */}
      navigate('/login', { replace: true });
    }
  });
</script>

{#if $isLoading}
  <div class="boot-splash">
    <p>Loading…</p>
  </div>
{:else if bootError}
  <div class="boot-error">
    <h1>Sign-in error</h1>
    <p>{bootError}</p>
    <a href="/login">Try again</a>
  </div>
{:else}
  <Router>
    <Shell>
      {#each sortedRoutes as route (route.routeDir + '|' + route.importPath)}
        {@const Component = resolveComponent(route.importPath)}
        {#if Component}
          <Route path={route.routeDir === '' ? '/' : `/${route.routeDir}`}>
            {#if PUBLIC_ROUTES.has(route.routeDir)}
              <svelte:component this={Component} />
            {:else}
              <AuthGuard>
                <svelte:component this={Component} />
              </AuthGuard>
            {/if}
          </Route>
        {/if}
      {/each}

      <!-- Fallback for unknown paths. Authenticated visitors land on the
           dashboard; anonymous ones are sent to /login by AuthGuard. -->
      <Route path="*">
        <AuthGuard>
          {@const NotFound = resolveComponent('./pages/Index.svelte')}
          <svelte:component this={NotFound} />
        </AuthGuard>
      </Route>
    </Shell>
  </Router>
{/if}

<style>
  .boot-splash,
  .boot-error {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem;
    font-family: system-ui, -apple-system, sans-serif;
  }
  .boot-error a {
    margin-top: 1rem;
    color: #2563eb;
    text-decoration: underline;
  }
</style>
